import React, { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { pushToGTM } from '~/actions/gtmActions';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import { IState } from '~/sharedInterfaces/IState';
import { IDropDownOption } from '~/sharedInterfaces/SharedInterfaces';
import { getInputClassNames, hashCode } from '~/utils';
import { getDefaultLabelName } from '../../../services/DefaultLabelNameService';
import { IBaseComponent } from '../../../sharedInterfaces/IBaseComponent';
import ComponentError from '../../ComponentError/ComponentError';
import Tooltip from '../Tooltip/Tooltip';

interface IDropDownProps extends IBaseComponent {
  label?: string;
  value: string | number;
  dropDownValues: IDropDownOption[];
  analyticsEvent: string;
  handleValidation?: () => void;
  tooltip?: boolean;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
}

const DropDown: FC<IDropDownProps> = ({
  componentName,
  label,
  value,
  dropDownValues,
  onChange,
  handleValidation,
  error,
  analyticsEvent,
  tooltip,
}) => {
  const { locale, settings } = useSelector((state: IState) => state.widgetContext);
  const componentLabel = label || getDefaultLabelName(locale, componentName).label;
  const componentTooltipText = getDefaultLabelName(locale, componentName).tooltipText;

  const uniqueKey = hashCode(`${settings.id}${componentName}`);
  const dispatch = useDispatch();

  const handler = (event: ChangeEvent<HTMLSelectElement>) => {
    if (onChange) onChange(event);

    dispatch(logUserFirstInteraction());

    dispatch(
      pushToGTM({
        event: `${analyticsEvent}-change`,
        [analyticsEvent]: event.currentTarget.value,
        [`input-type-${analyticsEvent}`]: 'dropDown',
      })
    );
  };

  useEffect(() => {
    if (handleValidation) handleValidation();
  }, [value]);

  return (
    <div
      className={getInputClassNames({
        tooltip,
        error,
        componentName,
        additionalClassName: 'qred-dropdown',
      })}
    >
      {componentTooltipText && <Tooltip tooltip={tooltip} tooltipText={componentTooltipText} />}
      <div className="qred-group">
        <div className="qred-row">
          <label htmlFor={`${uniqueKey}-dropdown`}>
            <span className="qred-first">{componentLabel}</span>
          </label>
          <select
            name={`${uniqueKey}-dropdown`}
            id={`${uniqueKey}-dropdown`}
            value={value}
            onChange={handler}
          >
            {dropDownValues.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error && <ComponentError error={error} />}
        </div>
      </div>
    </div>
  );
};

export default DropDown;
